import React, { useEffect, useRef, useState } from 'react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'
import { UploadIcon } from 'lucide-react'

import { toDollar } from 'utils'

import { Modal, Button, Alert } from '@repo/ui'
import { ColorPicker } from 'components/input/fields/ColorPicker'
import { SegmentedTabs } from 'components/navigation'
import { CUSTOMIZATION_SETUP_FEE } from 'contexts/CartContext'

import { TagCanvasEditor, type LogoPlacement } from './TagCanvasEditor'
import { computeLogoPreflight } from './logoPreflight'

// ─── Types ──────────────────────────────────────────────────────────────────

export type TagFinish = 'matte' | 'gloss'

/** The logo placed on one face of the tag. */
export interface TagSideLogo {
  logoDataUrl: string | null
  logoName: string | null
  logoPlacement: LogoPlacement | null
}

export interface TagCustomization {
  color: string
  /** Finish of the tag surface itself. */
  finish: TagFinish
  /** Finish of the printed logo. */
  logoFinish: TagFinish
  // Front face (kept top-level for back-compat). Data-URL + name + placement.
  logoDataUrl: string | null
  logoName: string | null
  /** Where + how big the front logo sits on the tag canvas (null until placed). */
  logoPlacement: LogoPlacement | null
  /** Back face — only used when the tag has 2 printable sides (custom.sides). */
  back?: TagSideLogo | null
  /** VRG-142 — print a QR code alongside the NFC (fallback for non-NFC phones). */
  qr?: boolean
}


// Fallback canvas when a product has no custom.width/height/shape metafields.
// A neutral square — don't assume a round tag when the shape is unknown.
const DEFAULT_CANVAS = { widthMm: 30, heightMm: 30, shape: 'rect' as const }

interface CustomizeTagModalProps {
  open: boolean
  onClose: () => void
  /** Existing customization to pre-fill when editing. */
  value: TagCustomization | null
  onSave: (customization: TagCustomization) => void
  onRemove?: () => void
  /** Printable tag canvas from the product's custom.width/height/shape metafields. */
  canvasWidthMm?: number | null
  canvasHeightMm?: number | null
  canvasShape?: string | null
  /** Printable sides (1 or 2) from custom.sides — 2 shows a Front/Back selector. */
  canvasSides?: number | null
  /** VRG-156 — render the placed logo with an embroidered (stitched) look when the
      Woven "Embroidered" finish is selected, rather than a flat print. */
  embroidered?: boolean
  /** VRG-156 — Woven tags are embroidered, never matte/gloss, so the "Logo finish"
      Matte/Gloss row is hidden for them (item 6). */
  isWoven?: boolean
  /** VRG-156 — the brand's saved logos (reusable across tag products); shown as a
      pick-to-reuse strip. */
  savedLogos?: { id: string; name: string; dataUrl: string }[]
  /** Called when a NEW logo is uploaded, to add it to the brand library. */
  onUploadLogo?: (dataUrl: string, name: string) => void
}

const DEFAULT: TagCustomization = {
  color: '#0f0f12',
  finish: 'matte',
  logoFinish: 'matte',
  logoDataUrl: null,
  logoName: null,
  logoPlacement: null,
  qr: false,
}

// ─── Component ──────────────────────────────────────────────────────────────

export const CustomizeTagModal = ({
  open,
  onClose,
  value,
  onSave,
  onRemove,
  canvasWidthMm,
  canvasHeightMm,
  canvasShape,
  canvasSides,
  embroidered = false,
  isWoven = false,
  savedLogos = [],
  onUploadLogo,
}: CustomizeTagModalProps) => {
  const theme = useTheme()
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [draft, setDraft] = useState<TagCustomization>(value ?? DEFAULT)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [activeSide, setActiveSide] = useState<'front' | 'back'>('front')

  // A tag has 1 or 2 printable faces (custom.sides); 2 shows a Front/Back selector.
  const sidesCount = canvasSides === 2 ? 2 : 1

  // Resolve the printable canvas from the product metafields (fall back to 30mm round).
  const widthMm = canvasWidthMm && canvasWidthMm > 0 ? canvasWidthMm : DEFAULT_CANVAS.widthMm
  const heightMm = canvasHeightMm && canvasHeightMm > 0 ? canvasHeightMm : DEFAULT_CANVAS.heightMm
  const shapeStr = (canvasShape ?? '').toLowerCase()
  const shape: 'round' | 'rect' = shapeStr.startsWith('rect')
    ? 'rect'
    : shapeStr === 'round'
      ? 'round'
      : DEFAULT_CANVAS.shape

  // Re-seed the draft every time the modal opens (§ item-page-modals-preselect-on-open).
  useEffect(() => {
    if (open) {
      setDraft(value ?? DEFAULT)
      setUploadError(null)
      setActiveSide('front')
    }
  }, [open, value])

  // Front logo lives at the top level. The back MIRRORS the front until the user
  // customises it, so `draft.back === null` means "same as front".
  const frontLogo: TagSideLogo = {
    logoDataUrl: draft.logoDataUrl,
    logoName: draft.logoName,
    logoPlacement: draft.logoPlacement,
  }

  const activeLogo: TagSideLogo =
    activeSide === 'back' ? draft.back ?? frontLogo : frontLogo

  const setActiveLogo = (patch: Partial<TagSideLogo>) =>
    setDraft(d => {
      if (activeSide === 'front') return { ...d, ...patch }
      // Editing the back materialises it from the current front on first change.
      const currentFront: TagSideLogo = {
        logoDataUrl: d.logoDataUrl,
        logoName: d.logoName,
        logoPlacement: d.logoPlacement,
      }
      return { ...d, back: { ...(d.back ?? currentFront), ...patch } }
    })

  const handleLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Logos must be PNG or SVG (transparency + print quality). `accept` only
    // hints the picker, so enforce it here for drag-drop / all-files selections.
    const name = file.name.toLowerCase()
    const isPngOrSvg =
      file.type === 'image/png' ||
      file.type === 'image/svg+xml' ||
      name.endsWith('.png') ||
      name.endsWith('.svg')
    if (!isPngOrSvg) {
      setUploadError('Logo must be a PNG or SVG file.')
      e.target.value = '' // let the user re-pick the same file after converting
      return
    }
    setUploadError(null)
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result)
      applyLogo(dataUrl, file.name)
      // Add the uploaded logo to the brand library so it can be reused on any tag.
      onUploadLogo?.(dataUrl, file.name)
    }
    reader.readAsDataURL(file)
  }

  // Load a logo (uploaded or picked from the saved library) onto the active face —
  // captures source pixel dims for print pre-flight, then centres it at a default size.
  const applyLogo = (dataUrl: string, name: string) => {
    const img = new Image()
    img.onload = () =>
      setActiveLogo({
        logoDataUrl: dataUrl,
        logoName: name,
        logoPlacement: {
          xPct: 50,
          yPct: 50,
          scalePct: 40,
          rotation: 0,
          naturalWidth: img.naturalWidth || 300,
          naturalHeight: img.naturalHeight || 300,
        },
      })
    img.src = dataUrl
  }

  // Print pre-flight for one side's logo (empty result when nothing is placed).
  const preflightFor = (logo: TagSideLogo) => {
    if (!logo.logoDataUrl || !logo.logoPlacement) {
      return { errors: [] as string[], warnings: [] as string[], dpi: null }
    }
    const isSvg =
      /^data:image\/svg\+xml/i.test(logo.logoDataUrl) ||
      (logo.logoName ?? '').toLowerCase().endsWith('.svg')
    return computeLogoPreflight({
      isSvg,
      naturalWidth: logo.logoPlacement.naturalWidth,
      naturalHeight: logo.logoPlacement.naturalHeight,
      scalePct: logo.logoPlacement.scalePct,
      xPct: logo.logoPlacement.xPct,
      yPct: logo.logoPlacement.yPct,
      shape,
      canvasWidthMm: widthMm,
      canvasHeightMm: heightMm,
    })
  }

  const backLogo = draft.back ?? frontLogo // back mirrors front until customised
  const preflight = preflightFor(activeLogo) // shown for the selected side
  const frontBlocked = preflightFor(frontLogo).errors.length > 0
  const backBlocked = sidesCount === 2 && preflightFor(backLogo).errors.length > 0
  // A blocking error on EITHER face prevents saving.
  const hasBlockingErrors = frontBlocked || backBlocked

  const finishOption = (field: 'finish' | 'logoFinish', key: TagFinish, label: string) => {
    const selected = draft[field] === key
    return (
      <Box
        onClick={() => setDraft(d => ({ ...d, [field]: key }))}
        sx={{
          flex: 1,
          cursor: 'pointer',
          textAlign: 'center',
          py: 2,
          borderRadius: 2,
          border: `1px solid ${selected ? theme.palette.brand[500] : theme.palette.primary[100]}`,
          backgroundColor: selected ? alpha(theme.palette.brand[500], 0.1) : undefined,
          '&:hover': { borderColor: theme.palette.brand[500] },
        }}
      >
        <Typography variant="body2">{label}</Typography>
      </Box>
    )
  }

  return (
    <Modal
      title="Customize your tag"
      open={open}
      onClose={onClose}
      actions={
        <>
          {value && onRemove && (
            <Button variant="secondary" destructive="hover" onClick={onRemove}>
              Remove
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(sidesCount === 1 ? { ...draft, back: null } : draft)}
            disabled={hasBlockingErrors}
          >
            Save customization
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {/* Tag color */}
        <Stack spacing={2}>
          <Typography variant="body2">Tag color</Typography>
          <ColorPicker
            value={draft.color}
            onChange={hex => setDraft(d => ({ ...d, color: hex }))}
          />
        </Stack>

        {/* Tag finish — hidden for Woven tags (fabric/thread, never matte/gloss). VRG-156. */}
        {!isWoven && (
          <Stack spacing={2}>
            <Typography variant="body2">Tag finish</Typography>
            <Stack direction="row" spacing={2}>
              {finishOption('finish', 'matte', 'Matte')}
              {finishOption('finish', 'gloss', 'Gloss')}
            </Stack>
          </Stack>
        )}

        {/* TODO(VRG-144 / Printed-QR-addon): hidden until we have QR-printing
            capability. When built, per product feedback (2026-07-27): render this
            UNDER the Logo section on the FRONT side, copy = "Replaces the logo on
            one side of the tag", and use the standard Switch treatment (no green
            focus thumb). Data plumbing (draft.qr → cart customization.qr → $100
            setup) already exists; only the toggle UI is hidden. Tracked on the
            product roadmap: .vergence/work/tasks/VRG-144-Tag.Shop.Printed.QR.Addon.md */}

        {/* Logo */}
        <Stack spacing={2}>
          <Typography variant="body2">Logo</Typography>

          {/* Front / Back toggle — only for 2-sided tags. The back mirrors the
              front until edited; "Reset to match front" re-links it. */}
          {sidesCount === 2 && (
            <Stack spacing={2}>
              <SegmentedTabs
                fullWidth
                tabs={[
                  { label: 'Front', value: 'front' },
                  { label: 'Back', value: 'back' },
                ]}
                value={activeSide}
                onChange={v => setActiveSide(v as 'front' | 'back')}
              />
              {activeSide === 'back' && draft.back && (
                <Button
                  variant="text"
                  onClick={() => setDraft(d => ({ ...d, back: null }))}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Reset to match front
                </Button>
              )}
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            onClick={() => fileRef.current?.click()}
            sx={{
              cursor: 'pointer',
              p: 3,
              borderRadius: 2,
              border: `1px dashed ${theme.palette.primary[200]}`,
              '&:hover': { borderColor: theme.palette.brand[500] },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: 2,
                backgroundColor: theme.palette.grey[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {activeLogo.logoDataUrl ? (
                <Box component="img" src={activeLogo.logoDataUrl} alt={activeLogo.logoName ?? 'logo'} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <UploadIcon size={20} strokeWidth={1} />
              )}
            </Box>
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap>
                {activeLogo.logoName ?? 'Upload a logo'}
              </Typography>
              <Typography variant="body3" color="text.secondary">
                {activeLogo.logoDataUrl
                  ? 'Tap to replace. Place it on the tag below.'
                  : 'PNG or SVG, transparent background recommended.'}
              </Typography>
            </Stack>
          </Stack>
          <input
            ref={fileRef}
            type="file"
            accept=".png,.svg,image/png,image/svg+xml"
            hidden
            onChange={handleLogo}
          />

          {/* Brand logo library — reuse a logo saved from ANY tag product (VRG-156).
              Click a saved logo to drop it onto this tag, then position it. */}
          {savedLogos.length > 0 && (
            <Stack spacing={1.5}>
              <Typography variant="body3" color="text.secondary">Your logos</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {savedLogos.map(l => (
                  <Box
                    key={l.id}
                    onClick={() => applyLogo(l.dataUrl, l.name)}
                    title={l.name}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.primary[100]}`,
                      backgroundColor: theme.palette.background.paper,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      p: 1,
                      '&:hover': {
                        borderColor: theme.palette.brand[600],
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.brand[600], 0.06)}`,
                      },
                    }}
                  >
                    <Box component="img" src={l.dataUrl} alt={l.name} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}

          {uploadError && <Alert severity="error" description={uploadError} sx={{ p: 3 }} />}

          {/* Place the logo on the tag — captures an exact transform, no design round-trip. */}
          {activeLogo.logoDataUrl && activeLogo.logoPlacement && (
            <>
              <TagCanvasEditor
                shape={shape}
                widthMm={widthMm}
                heightMm={heightMm}
                tagColor={draft.color}
                finish={draft.finish}
                logoFinish={draft.logoFinish}
                embroidered={embroidered}
                logoDataUrl={activeLogo.logoDataUrl}
                placement={activeLogo.logoPlacement}
                onChange={p => setActiveLogo({ logoPlacement: p })}
              />

              {/* Print pre-flight — red errors, amber warnings, soft brand tips. */}
              {(preflight.errors.length > 0 ||
                preflight.warnings.length > 0 ||
                preflight.tips.length > 0) && (
                // spacing matches the modal body (4) so the gap between these alerts and
                // the lead-time notice below is uniform — no tight-then-loose inconsistency.
                <Stack spacing={4}>
                  {preflight.errors.map((m, i) => (
                    <Alert key={`e${i}`} severity="error" description={m} sx={{ p: 3 }} />
                  ))}
                  {preflight.warnings.map((m, i) => (
                    <Alert key={`w${i}`} severity="warning" description={m} sx={{ p: 3 }} />
                  ))}
                  {preflight.tips.map((m, i) => (
                    <Alert key={`t${i}`} severity="info" description={m} sx={{ p: 3 }} />
                  ))}
                </Stack>
              )}
            </>
          )}
        </Stack>

        {/* Logo finish — hidden for Woven tags, whose logo IS the embroidery (VRG-156). */}
        {!isWoven && (
          <Stack spacing={2}>
            <Typography variant="body2">Logo finish</Typography>
            <Stack direction="row" spacing={2}>
              {finishOption('logoFinish', 'matte', 'Matte')}
              {finishOption('logoFinish', 'gloss', 'Gloss')}
            </Stack>
          </Stack>
        )}

        {/* VRG-575: the copy used to read "+$100 one-time" over a body line saying
            "charged once per product". Both were true only in the narrow sense of
            "does not scale with quantity" — the fee actually recurred on every
            order, and on every customised line within an order. A reader took
            "one-time" to mean once, ever. It now says which. */}
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2">Customization</Typography>
            <Typography variant="body2">
              +{toDollar(CUSTOMIZATION_SETUP_FEE)} per artwork
            </Typography>
          </Stack>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            A setup fee for originating your artwork. It is charged once for a given
            design, at any quantity. Reordering the same design costs nothing extra,
            and uploading new or changed artwork is a new setup.
          </Typography>
        </Stack>

        {/* Lead-time notice — standard @repo/ui info Alert (VRG-156 item 5), so it
            matches every other info alert in the app rather than a bespoke box. */}
        <Alert
          severity="info"
          description="Custom orders take about 4 weeks to produce. Your logo prints exactly as placed here, so there is no design review to wait on."
        />
      </Stack>
    </Modal>
  )
}
